import React, { useEffect } from 'react';
import { create } from 'zustand';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useApptekster } from '../../contexts/ApptekstContext';
import { useApi } from '../../hooks/useApi';
import {
    Kjoretoy,
    sendKnytning,
    sendSjekkKnytning,
    SoknadStatus,
} from '../../utils/api';
import {
    KjennemerkeSkjemaData,
    SjekkKjoretoySkjema,
} from './SjekkKjoretoySkjema';
import { HotjarMaskert } from '../../components/HotjarMaskert';
import { Paragraph } from '../../components/Paragraph';
import { useForm } from 'react-hook-form';
import { SVVBreadcrumbLink } from '@komponentkassen/svv-breadcrumbs';
import { SVVLayoutBanner } from '@komponentkassen/svv-layout';
import { GodtaVilkarSkjema } from '../../components/GodtaVilkarSkjema';

type KnytningState = {
    rettighetId: string;
    kjennemerke: string;
    tegnkombinasjon: string;
    kjoretoy?: Kjoretoy;
    dato?: string;
    setRettighetId: (rettighetId: string) => void;
    setKjennemerke: (kjennemerke: string) => void;
    setTegnkombinasjon: (tegnkombinasjon: string) => void;
    setDato: (dato: string) => void;
    setKjoretoy: (kjoretoy: Kjoretoy) => void;
    knytning?: SoknadStatus;
    setKnytningState: (knytning: SoknadStatus) => void;
};

export const useKnytningState = create<KnytningState>((set) => ({
    rettighetId: '',
    kjennemerke: '',
    tegnkombinasjon: '',
    dato: '',
    kjoretoy: undefined,
    knytning: undefined,
    setRettighetId: (rettighetId: string) => set(() => ({ rettighetId })),
    setKjennemerke: (kjennemerke: string) => set(() => ({ kjennemerke })),
    setTegnkombinasjon: (tegnkombinasjon: string) =>
        set(() => ({ tegnkombinasjon })),
    setDato: (dato: string) => set(() => ({ dato })),
    setKjoretoy: (kjoretoy: Kjoretoy) => set(() => ({ kjoretoy })),
    setKnytningState: (knytning: SoknadStatus) => set(() => ({ knytning })),
}));

export const KnyttSkilt: React.FC = () => {
    const t = useApptekster();
    const navigate = useNavigate();
    let { tegnkombinasjon } = useParams<{ tegnkombinasjon: string }>();

    const skjema = useForm<KjennemerkeSkjemaData>();

    const kjennemerke = useKnytningState((state) => state.kjennemerke);
    const rettighetId = useKnytningState((state) => state.rettighetId);
    const setTegnkombinasjon = useKnytningState(
        (state) => state.setTegnkombinasjon,
    );
    const setKjoretoy = useKnytningState((state) => state.setKjoretoy);
    const setKnytning = useKnytningState((state) => state.setKnytningState);

    const { hent, data, laster } = useApi(sendKnytning, {
        id: 'sendKnytning',
    });

    const api = useApi(sendSjekkKnytning, { id: kjennemerke });

    const kanKnyttes = api.data?.ok && !skjema.formState.isDirty;

    const bekreftelsestekst = `${t(
        'personlig-kjennemerke.knytt.vilkar.del1',
    )} ${tegnkombinasjon} ${t(
        'personlig-kjennemerke.knytt.vilkar.del2',
    )} ${kjennemerke} (${api.data?.kjoretoy?.merkenavn} ${
        api.data?.kjoretoy?.modell
    }). ${t('personlig-kjennemerke.knytt.vilkar.del3')}`;

    useEffect(() => {
        if (data?.ok && api?.data?.kjoretoy) {
            setKnytning(data);
            setKjoretoy(api.data.kjoretoy);
            navigate('/knytning-bekreftelse');
        }
    }, [navigate, data, api.data, setKnytning, setKjoretoy]);

    if (!rettighetId) {
        return <Navigate to="/statusoversikt" />;
    }

    return (
        <>
            <SVVLayoutBanner>
                <SVVBreadcrumbLink
                    href="#/statusoversikt"
                    ariaLabel={t('personlig-kjennemerke.snarvei.status')}
                    className="intern-link"
                >
                    {t('personlig-kjennemerke.snarvei.status')}
                </SVVBreadcrumbLink>
                <h1>
                    {t('personlig-kjennemerke.knytt.overskrift')}{' '}
                    <HotjarMaskert>{tegnkombinasjon}</HotjarMaskert>
                </h1>
            </SVVLayoutBanner>
            <SVVLayoutBanner secondary>
                <h2 className="spacing-bottom--small">
                    {t('personlig-kjennemerke.knytt.velg-kjoretoy')}
                </h2>
                <p className="spacing-bottom--small">
                    {t('personlig-kjennemerke.knytt.ingress', tegnkombinasjon)}
                </p>
                <SjekkKjoretoySkjema api={api} />

                {kanKnyttes && (
                    <>
                        <div className="spacing-bottom--medium spacing-top--small">
                            <p className="svv-form-label">
                                {t(
                                    'personlig-kjennemerke.knytt.valgt-kjoretoy',
                                )}
                            </p>
                            <Paragraph>
                                <HotjarMaskert>{kjennemerke}</HotjarMaskert> (
                                <HotjarMaskert>
                                    {api.data?.kjoretoy.merkenavn}
                                </HotjarMaskert>{' '}
                                <HotjarMaskert>
                                    {api.data?.kjoretoy.modell}
                                </HotjarMaskert>
                                )
                            </Paragraph>
                        </div>
                        <GodtaVilkarSkjema
                            bekreftelseslabel={t(
                                'personlig-kjennemerke.soknad.bekreftelse',
                            )}
                            handlingsknapp={t(
                                'personlig-kjennemerke.knytt.vilkar.bekreft',
                            )}
                            bekreftelsestekst={bekreftelsestekst}
                            onSubmit={() => {
                                hent({
                                    kjennemerke,
                                    rettighetId,
                                });
                                setTegnkombinasjon(tegnkombinasjon ?? '');
                            }}
                            laster={laster}
                            avbrytKnapp={() => navigate(-1)}
                        />
                    </>
                )}
            </SVVLayoutBanner>
        </>
    );
};
